import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import SectionHeading from "@/components/base/SectionHeading";
import ShareModal from "./components/ShareModal";
import { getDramaById, getRelatedDramas, formatDate } from "@/utils/dramaUtils";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";

export default function DetailPage() {
  const { id = "" } = useParams();
  const drama = getDramaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: drama ? `${drama.title} - 剧情介绍与资讯 - 赏金女王` : "作品详情 - 赏金女王影视大全",
    description: drama
      ? `${drama.title}（${drama.year} / ${drama.region}）：${drama.synopsis.slice(0, 90)}`
      : "影视作品详情页",
    keywords: drama ? `${drama.title},${drama.genres.join(",")},赏金女王,剧情介绍` : "赏金女王",
  });

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
          <i className="ri-file-unknow-line" aria-hidden="true"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-600">该作品可能已下架或链接有误</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/"
            className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
          <Link
            to="/category/tv"
            className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            浏览电视剧
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedDramas(drama);
  const categoryMeta = categories.find((c) => c.key === drama.category);
  const favorited = isFavorite(drama.id);

  const metaList = [
    { label: "类型", value: drama.genres.join(" / ") },
    { label: "主演", value: drama.cast.join(" / ") },
    { label: "导演", value: drama.director },
    { label: "地区", value: drama.region },
    { label: "年份", value: String(drama.year) },
    { label: "状态", value: drama.status },
  ];

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-8 py-6 md:py-10">
          <div className="mb-6">
            <Breadcrumb
              tone="dark"
              items={[
                { name: "首页", path: "/" },
                { name: categoryMeta?.name ?? "影视", path: `/category/${drama.category}` },
                { name: drama.title },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-900">
                <img
                  src={drama.cover}
                  alt={`${drama.title} 海报`}
                  title={`${drama.title} 剧情介绍`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
                {drama.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="flex items-center gap-1 text-accent-400 font-medium">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {drama.rating.toFixed(1)} 分
                </span>
                <span className="px-2 py-0.5 rounded-md bg-background-50/10 text-background-100">
                  {drama.type}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-background-50/10 text-background-100">
                  {drama.region} · {drama.year}
                </span>
                <span className="text-primary-200">更新：{formatDate(drama.updateTime)}</span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl">
                {metaList.map((m) => (
                  <div key={m.label} className="flex gap-3 text-sm py-1 border-b border-primary-800/60">
                    <span className="w-12 shrink-0 text-primary-300">{m.label}</span>
                    <span className="text-background-100">{m.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${drama.category}`}
                    className="px-2.5 py-1 rounded-md text-xs bg-secondary-900/60 text-secondary-100 hover:bg-accent-500 hover:text-primary-950 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(drama.id)}
                  className={`px-5 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    favorited
                      ? "bg-accent-500 text-primary-950"
                      : "border border-background-50/40 text-background-50 hover:bg-background-50/10"
                  }`}
                >
                  <i
                    className={favorited ? "ri-heart-fill" : "ri-heart-line"}
                    aria-hidden="true"
                  ></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="px-5 py-2.5 rounded-md text-sm font-medium border border-background-50/40 text-background-50 hover:bg-background-50/10 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-line" aria-hidden="true"></i>
                  分享
                </button>
                <span className="text-xs text-primary-300">收藏为本地演示，无需登录</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-12">
        <section>
          <SectionHeading title="剧情简介" subtitle="深入了解这部作品的故事脉络" />
          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-8">
            <p className="text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
              {drama.synopsis}
            </p>
            <h3 className="mt-6 mb-3 font-heading text-lg font-bold text-foreground-950">剧情看点</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                `题材类型：${drama.genres.join(" · ")}`,
                `主演阵容：${drama.cast.join(" / ")}`,
                `导演执导：${drama.director}`,
                `制作地区：${drama.region}（${drama.year}）`,
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2 text-sm text-foreground-700 bg-background-100/60 rounded-md px-3 py-2.5"
                >
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5" aria-hidden="true"></i>
                  {line}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-foreground-400">
              * 以上剧情介绍与看点内容仅供影视资讯参考，本站不提供真实播放与下载功能。
            </p>
          </div>
        </section>

        <section>
          <SectionHeading
            title="相关推荐"
            subtitle="看过这部作品的人还喜欢"
            moreText="更多同类"
            morePath={`/category/${drama.category}`}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.slice(0, 4).map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </section>
      </div>

      {shareOpen && <ShareModal title={drama.title} onClose={() => setShareOpen(false)} />}
    </div>
  );
}